import { viewerStrings } from '../copy/strings.js';
import { message, hasText, optionalText } from '../copy/text.js';
import { useEffect, useState, useSyncExternalStore } from 'react';
import Alert from '@cloudscape-design/components/alert';
import type { RelationshipGroupReference } from '@cdr-clarity/contracts';
import type { UnifiedRecordsState } from '../state/unified-records-state.js';
import type { UnifiedRecordPage } from '../state/unified-record-page.js';
import {
  relationshipGroupRecords,
  type RelationshipDrillDown,
} from '../state/relationship-group-records.js';
import { cellHelp, cellText, fieldTitle } from '../presentation/cells.js';

export function RelationshipDrilldown({
  reference,
  store,
  drillDown,
  close,
}: {
  reference: RelationshipGroupReference;
  store: UnifiedRecordsState;
  drillDown: RelationshipDrillDown;
  close(): void;
}) {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const fields = useSyncExternalStore(store.fields.subscribe, store.fields.getSnapshot);
  const [page, setPage] = useState<UnifiedRecordPage | null>(null);
  const [cursors, setCursors] = useState<readonly (readonly string[] | null)[]>([null]);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let active = true;
    setBusy(true);
    setError('');
    setPage(null);
    void Promise.resolve()
      .then(() =>
        relationshipGroupRecords(drillDown, reference, state.visibleFields, cursors[index]!),
      )
      .then((result) => {
        if (active) setPage(result);
      })
      .catch((reason: unknown) => {
        if (active) setError(String(reason));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [drillDown, reference, state.visibleFields, state.generation, cursors, index]);
  return (
    <section
      className="report-drilldown"
      aria-label={optionalText(
        viewerStrings.webComponentsRelationshipDrilldown.reportCallAndMediaSources,
      )}
      aria-busy={busy}
    >
      <div className="field-tools" data-copy-group>
        {hasText(viewerStrings.webComponentsRelationshipDrilldown.reportCallAndMediaSources2) ? (
          <strong>
            {viewerStrings.webComponentsRelationshipDrilldown.reportCallAndMediaSources2}
          </strong>
        ) : null}
        {hasText(viewerStrings.webComponentsRelationshipDrilldown.closeRecords) ? (
          <button data-testid="drilldown-close" onClick={close}>
            {viewerStrings.webComponentsRelationshipDrilldown.closeRecords}
          </button>
        ) : null}
      </div>
      {hasText(error) && <Alert type="error">{error}</Alert>}
      {busy &&
        (hasText(viewerStrings.webComponentsRelationshipDrilldown.loadingSourceRecords) ? (
          <p role="status">
            {viewerStrings.webComponentsRelationshipDrilldown.loadingSourceRecords}
          </p>
        ) : null)}
      {page && (
        <>
          {hasText(
            viewerStrings.webComponentsRelationshipDrilldown
              .callsMediaObservationsAssociationsUnmatchedMedia,
          ) ? (
            <p>
              {message(
                viewerStrings.webComponentsRelationshipDrilldown
                  .callsMediaObservationsAssociationsUnmatchedMedia,
                {
                  value1: page.result.counts.primaryRecords.toLocaleString(),
                  value2: ' ',
                  value3: page.result.counts.relatedRecords.toLocaleString(),
                  value4: ' ',
                  value5: page.result.counts.memberships.toLocaleString(),
                  value6: ' ',
                  value7: page.result.counts.unmatchedRelated.toLocaleString(),
                },
              )}
            </p>
          ) : null}
          <div className="report-table-overflow">
            <table>
              <thead>
                <tr>
                  {state.visibleFields.map((field) => (
                    <th key={field.id}>{fieldTitle(field, fields.preference.timezone)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.result.rows.map((row) => (
                  <tr key={row.membershipId}>
                    {state.visibleFields.map((field) => (
                      <td
                        key={field.id}
                        title={optionalText(
                          cellHelp(row.cells[field.id], field, fields.preference.timezone),
                        )}
                      >
                        {cellText(row.cells[field.id], field, fields.preference.timezone)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="field-tools" data-copy-group>
            {hasText(viewerStrings.webComponentsRelationshipDrilldown.previousRecords) ? (
              <button
                data-testid="drilldown-previous"
                disabled={busy || index === 0}
                onClick={() => setIndex(index - 1)}
              >
                {viewerStrings.webComponentsRelationshipDrilldown.previousRecords}
              </button>
            ) : null}
            {hasText(viewerStrings.webComponentsRelationshipDrilldown.page) ? (
              <span>
                {message(viewerStrings.webComponentsRelationshipDrilldown.page, {
                  value1: index + 1,
                })}
              </span>
            ) : null}
            {hasText(viewerStrings.webComponentsRelationshipDrilldown.nextRecords) ? (
              <button
                data-testid="drilldown-next"
                disabled={busy || !page.nextCursors}
                onClick={() => {
                  setCursors([...cursors.slice(0, index + 1), page.nextCursors]);
                  setIndex(index + 1);
                }}
              >
                {viewerStrings.webComponentsRelationshipDrilldown.nextRecords}
              </button>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
